/**
 * Regrouping a partner under a different parent.
 *
 * The preview is a POST rather than a GET because the server needs the proposed
 * parent in a body, and it computes the answer by simulating the move inside a
 * rolled-back transaction - so a URL could not express it and a GET would be
 * misleading about the work involved. It is still read-only, so it is modelled
 * as a query rather than a mutation: the dialog wants loading and error states,
 * and re-picking the same parent should not re-run the simulation.
 *
 * Registered as CONTEXT in `query-defaults` and never persisted: this drives a
 * confirm step that changes who can see what, and a preview restored from disk
 * could describe a structure that has since moved on.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useOrg } from '@/admin/hooks/use-org'
import api from '@/admin/lib/api'

export interface RegroupPreview {
  organisation_name: string
  previous_parent_name: string | null
  /** Partnership uuid to reverse to, or '' when it was already top level. */
  previous_parent_partnership_uuid: string
  new_parent_name: string | null
  registrations_affected: number
  stale_grants_to_remove: number
  allowed: boolean
  blocked_reason: string
}

/**
 * `parentUuid === null` previews a promotion to top level, which is a real
 * choice - so "no parent selected yet" is `undefined`, not null, and only that
 * disables the query.
 */
export function useRegroupPreview(
  partnershipUuid: string | undefined,
  parentUuid: string | null | undefined,
) {
  const { orgUuid } = useOrg()
  return useQuery<RegroupPreview>({
    queryKey: ['partnership-regroup-preview', orgUuid, partnershipUuid, parentUuid],
    queryFn: () =>
      api
        .post(
          `/organisations/${orgUuid}/partnerships/${partnershipUuid}/reparent/`,
          { parent_partnership_uuid: parentUuid, dry_run: true },
        )
        .then((r) => r.data),
    enabled: !!orgUuid && !!partnershipUuid && parentUuid !== undefined,
  })
}

export function useApplyRegroup(partnershipUuid: string | undefined) {
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (parentUuid: string | null) =>
      api
        .post(
          `/organisations/${orgUuid}/partnerships/${partnershipUuid}/reparent/`,
          { parent_partnership_uuid: parentUuid },
        )
        .then((r) => r.data),
    onSuccess: () => {
      // The move changes the partner list's grouping, this partner's detail,
      // and what the health scan considers stale - all of which are read from
      // caches that would otherwise keep showing the old structure.
      queryClient.invalidateQueries({ queryKey: ['partnerships'] })
      queryClient.invalidateQueries({ queryKey: ['partnership-detail'] })
      queryClient.invalidateQueries({ queryKey: ['partnership-regroup-preview'] })
    },
  })
}
